import { mapJobChange, unescapeDisplayMd } from "./writingAssistantUtils";

/**
 * Split original_md into blocks. Indices may be HTML element indexes from the API;
 * prefer content matching for replace placement (see resolveReplaceBlockIndex).
 */
export function splitMarkdownBlocks(md) {
	if (!md) return [];
	return md.split(/\n\n+/).filter((block) => block.trim());
}

/** Normalize text for comparing old_content to MD blocks. */
export function normalizeForMatch(text) {
	if (!text) return "";
	let s = unescapeDisplayMd(String(text), String(text));
	s = s.replace(/^#{1,6}\s*/gm, "");
	s = s.replace(/\s+/g, " ").trim();
	return s;
}

/** Compact form ignores all whitespace (handles "insightsthat" vs "insights that"). */
function normalizeCompact(text) {
	return normalizeForMatch(text).replace(/\s+/g, "").toLowerCase();
}

function oldContentCandidates(change) {
	const raw = change?.raw || {};
	const values = [
		raw.old_content_md,
		raw.old_content,
		change.oldDisplayMd,
		change.oldContent,
	];
	const out = [];
	const seen = new Set();
	for (const value of values) {
		const trimmed = String(value || "").trim();
		if (!trimmed || seen.has(trimmed)) continue;
		seen.add(trimmed);
		out.push(trimmed);
	}
	return out;
}

/**
 * Find which original_md block a replace change should swap out.
 * Prefer content match; use API index only as a hint / last resort.
 */
export function resolveReplaceBlockIndex(change, blocks, usedBlockIndexes) {
	const used = usedBlockIndexes || new Set();
	const candidates = oldContentCandidates(change);
	if (!candidates.length || !blocks.length) return null;

	const normalizedNeedles = candidates.map((c) => ({
		exact: normalizeForMatch(c),
		compact: normalizeCompact(c),
	}));

	const scored = [];
	for (let i = 0; i < blocks.length; i += 1) {
		if (used.has(i)) continue;
		const exactBlock = normalizeForMatch(blocks[i]);
		const compactBlock = normalizeCompact(blocks[i]);
		if (!exactBlock) continue;

		let score = 0;
		for (const needle of normalizedNeedles) {
			if (!needle.exact && !needle.compact) continue;
			if (needle.exact && exactBlock === needle.exact) {
				score = Math.max(score, 100);
			} else if (needle.compact && compactBlock === needle.compact) {
				score = Math.max(score, 90);
			} else if (
				needle.exact &&
				(exactBlock.includes(needle.exact) || needle.exact.includes(exactBlock))
			) {
				const ratio =
					Math.min(exactBlock.length, needle.exact.length) /
					Math.max(exactBlock.length, needle.exact.length);
				score = Math.max(score, 50 + Math.round(ratio * 30));
			} else if (
				needle.compact &&
				(compactBlock.includes(needle.compact) ||
					needle.compact.includes(compactBlock))
			) {
				const ratio =
					Math.min(compactBlock.length, needle.compact.length) /
					Math.max(compactBlock.length, needle.compact.length);
				score = Math.max(score, 40 + Math.round(ratio * 30));
			}
		}
		if (score > 0) scored.push({ index: i, score });
	}

	if (scored.length > 0) {
		const hint = change?.raw?.index;
		scored.sort((a, b) => {
			if (b.score !== a.score) return b.score - a.score;
			if (hint != null && Number.isFinite(Number(hint))) {
				return Math.abs(a.index - hint) - Math.abs(b.index - hint);
			}
			return a.index - b.index;
		});
		return scored[0].index;
	}

	// Last resort: trust API index only when that block is free and in range.
	const hint = change?.raw?.index;
	if (
		hint != null &&
		Number.isFinite(Number(hint)) &&
		hint >= 0 &&
		hint < blocks.length &&
		!used.has(hint)
	) {
		return Number(hint);
	}

	return null;
}

/**
 * Build an ordered list of preview segments: markdown blocks interleaved with
 * inline callouts, plus an optional AI additions footer for out-of-body inserts
 * and unmatched replaces (without dumping the full patched doc — that duplicates).
 */
export function buildPreviewSegments(originalMd, changes, patchedMd) {
	if (!changes?.length) {
		if (originalMd) return [{ type: "markdown", content: originalMd }];
		if (patchedMd) return [{ type: "markdown", content: patchedMd }];
		return [];
	}

	if (!originalMd) {
		if (patchedMd) return [{ type: "markdown", content: patchedMd }];
		return [];
	}

	const blocks = splitMarkdownBlocks(originalMd);
	const mapped = changes.map((change, index) =>
		mapJobChange(change, index, blocks.length)
	);

	const inlineChanges = mapped.filter((change) => !change.isOutOfBody);
	const outOfBodyChanges = mapped.filter((change) => change.isOutOfBody);

	const replaceAt = new Map();
	const insertAfter = new Map();
	const unmatchedReplaces = [];
	const usedBlockIndexes = new Set();

	const updates = inlineChanges
		.filter((change) => change.type === "updated")
		.slice()
		.sort((a, b) => {
			const ai = a.raw?.index ?? Number.MAX_SAFE_INTEGER;
			const bi = b.raw?.index ?? Number.MAX_SAFE_INTEGER;
			return ai - bi;
		});

	for (const change of updates) {
		const blockIndex = resolveReplaceBlockIndex(
			change,
			blocks,
			usedBlockIndexes
		);
		if (blockIndex == null) {
			unmatchedReplaces.push(change);
			continue;
		}
		usedBlockIndexes.add(blockIndex);
		replaceAt.set(blockIndex, change);
	}

	for (const change of inlineChanges) {
		if (change.type !== "added" || change.raw.after_index == null) continue;
		const key = change.raw.after_index;
		// Prefer inserting after a resolved replace that shared the same HTML index
		// when after_index is out of MD range — otherwise keep raw after_index.
		let insertKey = key;
		if (key < 0 || key >= blocks.length) {
			// Try to place after the block that matched a replace with nearby index
			const nearby = [...replaceAt.entries()].find(
				([, ch]) => ch.raw?.index === key || ch.raw?.index === key + 1
			);
			if (nearby) {
				insertKey = nearby[0];
			} else {
				unmatchedReplaces.push(change);
				continue;
			}
		}
		if (!insertAfter.has(insertKey)) insertAfter.set(insertKey, []);
		insertAfter.get(insertKey).push(change);
	}

	const segments = [];

	for (let i = 0; i < blocks.length; i += 1) {
		if (replaceAt.has(i)) {
			const change = replaceAt.get(i);
			segments.push({ type: "callout", changeIndex: change.id, change });
		} else {
			segments.push({ type: "markdown", content: blocks[i] });
		}

		if (insertAfter.has(i)) {
			for (const change of insertAfter.get(i)) {
				segments.push({ type: "callout", changeIndex: change.id, change });
			}
		}
	}

	if (outOfBodyChanges.length > 0) {
		segments.push({ type: "additions-header" });
		outOfBodyChanges
			.sort((a, b) => a.sortIndex - b.sortIndex)
			.forEach((change) => {
				segments.push({
					type: "callout",
					changeIndex: change.id,
					change,
				});
			});
	}

	// Unmatched replaces: show callout once at the end (never re-dump patched_md).
	unmatchedReplaces
		.sort((a, b) => a.sortIndex - b.sortIndex)
		.forEach((change) => {
			segments.push({
				type: "callout",
				changeIndex: change.id,
				change,
			});
		});

	return segments;
}
